import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid4 } from 'uuid';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import SearchInput from '@design-system/input/search-input';
import IconTextBlock from '@component/common/block/icon-text-block';
import Gap from '@component/common/gap';
import Line from '@component/common/line';
import Scroll from '@component/common/scroll';
import Canvas from '@design-system/canvas';
import TextButton from '@design-system/text-button';
import LoadingDots from '@design-system/loading-dots';
import { ETheme } from '@datatype/type';
import { loadKakaoMaps } from '@util/kakao-map';
import type { ILocation, IKakaoMap, IKakaoMaps, IKakaoMarker } from '@util/kakao-map';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  value: ILocation | null;
  handleConfirm: (location: ILocation) => void;
  handleCancel: () => void;
}

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;
const MapContainer = styled.div`
  width: 100%;
  height: min(35vh, 300px);
  min-height: 180px;
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_2};
  border-radius: ${size.BORDER_RADIUS_1};
  overflow: hidden;
`;
const ControlContainer = styled.div`
  display: flex;
  width: 100%;
  gap: ${size.MARGIN_1};
`;
const MapControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-end;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${size.MARGIN_1};
`;

const MapHintContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;
const SearchResultsContainer = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  width: 100%;
  z-index: 5;
  padding: 5px;
  background-color: ${color.WHITE_1};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_2};
  border-radius: ${size.BORDER_RADIUS_1};
  box-shadow: ${color.SURFACE_SHADOW};
`;
const SearchResultItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px;
`;

const SearchResultItemContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  cursor: pointer;
`;
const TEXT_DESIGN = {
  box: { width: '100%', alignX: 'left' as const },
  text: { size: 'small' as const, color: color.GRAY_1 },
};
type TMapPosition = { getLat(): number; getLng(): number };

// 지도·마커·줌 컨트롤을 생성하고, 클릭·드래그 이벤트와 크기 변경 감지를 연결한다.
// 반환한 dispose는 등록한 이벤트·observer와 지도 표시를 정리한다.
const createLocationMap = (
  maps: IKakaoMaps,
  element: HTMLDivElement,
  initial: ILocation | null,
  handlePick: (position: TMapPosition) => void,
) => {
  const center = new maps.LatLng(initial?.latitude ?? 37.5665, initial?.longitude ?? 126.978);
  const map = new maps.Map(element, { center, level: initial ? 3 : 7, scrollwheel: false });
  map.addControl(new maps.ZoomControl(), maps.ControlPosition.RIGHT);
  const marker = new maps.Marker({ position: center, map: initial ? map : undefined, draggable: true });
  const handleClick = (event: { latLng: TMapPosition }) => handlePick(event.latLng);
  const handleDrag = () => handlePick(marker.getPosition());
  const observer = new ResizeObserver(() => map.relayout());

  maps.event.addListener(map, 'click', handleClick);
  maps.event.addListener(marker, 'dragend', handleDrag);
  observer.observe(element);

  return {
    map,
    marker,
    dispose: () => {
      observer.disconnect();
      maps.event.removeListener(map, 'click', handleClick);
      maps.event.removeListener(marker, 'dragend', handleDrag);
      marker.setMap(null);
      element.replaceChildren();
    },
  };
};

const LocationPicker: React.FC<IProps> = ({ value, handleConfirm, handleCancel }) => {
  const mapElementRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<IKakaoMap | null>(null);
  const markerRef = useRef<IKakaoMarker | null>(null);
  const mapsRef = useRef<IKakaoMaps | null>(null);
  const initialValueRef = useRef(value);
  const requestRef = useRef(0);
  const searchRef = useRef(0);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(value?.name || value?.address || '');
  const [resultsOpen, setResultsOpen] = useState(false);
  const [selected, setSelected] = useState<ILocation | null>(value);
  const [results, setResults] = useState<ILocation[]>([]);
  const [ready, setReady] = useState(false);
  const [loadingError, setLoadingError] = useState('');
  const [retry, setRetry] = useState(0);
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState('');
  const [satellite, setSatellite] = useState(false);

  // 선택한 좌표로 핀을 옮기고 주소를 조회한다. 이전 선택의 늦은 응답은 무시한다.
  const handleLocationPick = useCallback((position: TMapPosition) => {
    const maps = mapsRef.current;
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!maps || !map || !marker) return;

    const request = ++requestRef.current;
    const latitude = position.getLat();
    const longitude = position.getLng();
    // 지도에서 직접 지정한 위치는 기존 검색 장소와 다를 수 있으므로 검색창을 비운다.
    ++searchRef.current;
    setQuery('');
    setResultsOpen(false);
    setSearching(false);
    setResults([]);
    setMessage('');
    marker.setPosition(position);
    marker.setMap(map);
    setSelected({ latitude, longitude, name: '', address: '' });

    new maps.services.Geocoder().coord2Address(longitude, latitude, (data, status) => {
      if (request !== requestRef.current) return;
      const address =
        status === maps.services.Status.OK
          ? data[0]?.road_address?.address_name || data[0]?.address?.address_name || ''
          : '';
      setSelected({ latitude, longitude, name: '', address });
    });
  }, []);

  // SDK 로드 후 지도를 초기화한다. 재시도·언마운트 시 기존 지도와 대기 중인 응답을 정리한다.
  useEffect(() => {
    let active = true;
    let locationMap: ReturnType<typeof createLocationMap> | undefined;
    setReady(false);
    setLoadingError('');
    setSatellite(false);

    const initialize = async () => {
      try {
        const maps = await loadKakaoMaps();
        if (!active || !mapElementRef.current) return;
        locationMap = createLocationMap(maps, mapElementRef.current, initialValueRef.current, handleLocationPick);
        mapsRef.current = maps;
        mapRef.current = locationMap.map;
        markerRef.current = locationMap.marker;
        setReady(true);
      } catch (error) {
        if (active) setLoadingError(error instanceof Error ? error.message : '지도를 불러오지 못했습니다.');
      }
    };
    void initialize();

    return () => {
      active = false;
      ++requestRef.current;
      ++searchRef.current;
      locationMap?.dispose();
      mapRef.current = null;
      markerRef.current = null;
      mapsRef.current = null;
    };
  }, [retry, handleLocationPick]);

  const handleSearch = useCallback((keyword: string) => {
    const maps = mapsRef.current;
    // 조합 중에도 자동 검색하되, ㅅ·ㅜ 같은 미완성 한글 자모는 검색하지 않는다.
    if (!maps || !keyword.trim() || /[ㄱ-ㅎㅏ-ㅣᄀ-ᇿ]/.test(keyword)) return;
    const request = ++searchRef.current;
    setResultsOpen(true);
    setSearching(true);
    setMessage('');
    setResults([]);
    const finish = (locations: ILocation[], error: boolean) => {
      if (request !== searchRef.current) return;
      setResults(locations);
      setSearching(false);
      setMessage(
        error
          ? '검색하지 못했습니다. 다시 시도해주세요.'
          : locations.length
            ? ''
            : '검색 결과가 없어요. 지도에서 직접 위치를 선택해주세요.',
      );
    };
    new maps.services.Places().keywordSearch(keyword.trim(), (places, status) => {
      if (request !== searchRef.current) return;
      if (status === maps.services.Status.OK) {
        finish(
          places.map((place) => ({
            name: place.place_name || '',
            address: place.road_address_name || place.address_name,
            latitude: Number(place.y),
            longitude: Number(place.x),
          })),
          false,
        );
      } else if (status === maps.services.Status.ZERO_RESULT) {
        new maps.services.Geocoder().addressSearch(keyword.trim(), (addresses, addressStatus) =>
          finish(
            addresses.map((address) => ({
              name: '',
              address: address.address_name,
              latitude: Number(address.y),
              longitude: Number(address.x),
            })),
            addressStatus !== maps.services.Status.OK && addressStatus !== maps.services.Status.ZERO_RESULT,
          ),
        );
      } else finish([], true);
    });
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: PointerEvent) => {
      if (event.target instanceof Node && !searchContainerRef.current?.contains(event.target)) {
        ++searchRef.current;
        setResultsOpen(false);
        setSearching(false);
      }
    };
    document.addEventListener('pointerdown', handleOutsideClick, true);
    return () => document.removeEventListener('pointerdown', handleOutsideClick, true);
  }, []);

  const selectResult = (location: ILocation) => {
    const maps = mapsRef.current;
    if (!maps || !mapRef.current || !markerRef.current) return;
    ++requestRef.current;
    ++searchRef.current;
    setResultsOpen(false);
    setSearching(false);
    const position = new maps.LatLng(location.latitude, location.longitude);
    markerRef.current.setPosition(position);
    markerRef.current.setMap(mapRef.current);
    mapRef.current.setCenter(position);
    mapRef.current.setLevel(3);
    setQuery(location.name || location.address);
    setSelected(location);
    setResults([]);
  };
  const SearchResultItems = results.map((location, index) => (
    <SearchResultItemContainer key={uuid4()} onClick={() => selectResult(location)}>
      {index > 0 && <Line direction='horizontal' width='100%' color={color.GRAY_2} margin={`5px 0px`} />}
      <TextBlock
        text={location.name || location.address}
        design={{
          box: { width: '100%', alignX: 'left' },
          text: { size: 'small', color: color.BLACK_4, bold: true, oneline: true },
        }}
      />
      {location.name && (
        <TextBlock
          text={location.address}
          design={{ box: { width: '100%', alignX: 'left' }, text: { size: 'tiny', color: color.GRAY_1 } }}
        />
      )}
    </SearchResultItemContainer>
  ));
  return (
    <Wrapper>
      <TextBlock text='현장 위치 찾기' design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }} />
      <Gap height={size.MARGIN_2} />
      <SearchContainer ref={searchContainerRef}>
        <SearchInput
          name='location-query'
          autoSearch
          value={query}
          placeholder='주소 또는 장소명 검색'
          theme={ETheme.CYAN}
          size='medium'
          width='100%'
          disabled={!ready}
          onChange={(value) => {
            ++searchRef.current;
            setSearching(false);
            setResults([]);
            setMessage('');
            setResultsOpen(false);
            setQuery(value);
          }}
          handleSearch={handleSearch}
        />
        {resultsOpen && (
          <SearchResultsContainer>
            {searching ? (
              <LoadingDots />
            ) : results.length > 0 ? (
              <Scroll direction='y' height='200px'>
                <SearchResultItemsContainer>{SearchResultItems}</SearchResultItemsContainer>
              </Scroll>
            ) : (
              <TextBlock text={message} design={TEXT_DESIGN} />
            )}
          </SearchResultsContainer>
        )}
      </SearchContainer>
      <Gap height={size.MARGIN_1} />
      {!ready && !loadingError && <LoadingDots />}
      {loadingError && (
        <>
          <TextBlock text={loadingError} design={TEXT_DESIGN} />
          <TextButton
            text='다시 시도'
            theme={ETheme.GRAY}
            kind='outlined'
            size='small'
            handleClick={() => setRetry((current) => current + 1)}
          />
        </>
      )}
      <MapControlContainer>
        <MapHintContainer>
          <TextBlock text='지도를 누르면 핀' design={{ text: { size: 'small', color: color.BLACK_2 } }} />
          <IconBlock icon='map-pin' design={{ icon: { size: 'tiny', color: color.CYAN_2 } }} />
          <TextBlock text='이 표시됩니다.' design={{ text: { size: 'small', color: color.BLACK_2 } }} />
        </MapHintContainer>
        <IconTextBlock
          icon={satellite ? 'map' : 'satellite'}
          text={satellite ? '일반지도' : '위성지도'}
          design={{
            box: {
              gap: '5px',
              padding: `2px 5px`,
              border: false,
              borderWidth: size.BORDER_WIDTH_1,
              borderColor: color.BLACK_2,
              borderRadius: size.BORDER_RADIUS_1,
              event: { cursor: 'pointer' },
            },
            text: { size: 'small', color: ready ? color.BLACK_2 : color.GRAY_2 },
            icon: { size: 'small', color: ready ? color.BLACK_2 : color.GRAY_2 },
          }}
          click={{
            handler: () => {
              const maps = mapsRef.current;
              if (!ready || !maps) return;
              mapRef.current?.setMapTypeId(satellite ? maps.MapTypeId.ROADMAP : maps.MapTypeId.HYBRID);
              setSatellite(!satellite);
            },
          }}
        />
      </MapControlContainer>
      <Gap height={size.MARGIN_1} />
      <MapContainer ref={mapElementRef} />
      <Gap height={size.MARGIN_1} />
      <Canvas theme={ETheme.WHITE} width='100%' padding={size.MARGIN_1} border>
        <IconTextBlock
          icon={selected ? 'map-pin' : 'map-pin-off'}
          text={selected ? selected.name || '선택한 위치' : '선택한 위치가 없어요'}
          design={{
            box: { width: '100%', alignX: 'left', gap: '5px' },
            text: { size: 'medium', color: color.BLACK_2, bold: true },
            icon: { size: 'small', color: color.BLACK_2 },
          }}
        />
        <Gap height='5px' />
        <TextBlock
          text={
            selected ? selected.address || '지도에서 지정한 위치' : '검색 결과를 선택하거나 지도에서 핀을 지정하세요.'
          }
          design={TEXT_DESIGN}
        />
      </Canvas>
      <Gap height={size.MARGIN_2} />
      <ControlContainer>
        <TextButton
          text='취소'
          shape='pill'
          width='100%'
          theme={ETheme.CYAN}
          kind='outlined'
          size='medium'
          handleClick={handleCancel}
        />
        <TextButton
          text='위치 설정'
          shape='pill'
          width='100%'
          theme={ETheme.CYAN}
          size='medium'
          disabled={!selected || !ready}
          handleClick={() => {
            if (selected) handleConfirm(selected);
          }}
        />
      </ControlContainer>
    </Wrapper>
  );
};
export default LocationPicker;
